import { Box, Flex, Icon, Input, InputProps, SelectProps, Stack, Text, useOutsideClick } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { HiChevronDown } from 'react-icons/hi';

import { SimpleField, SimpleXProps } from './SimpleField';
import { useSimpleFormContext } from './SimpleForm';

export type SimpleSelectProps<FormSchema extends FieldValues> = SimpleXProps<FormSchema> &
  Omit<SelectProps, 'name'> & {
    options: Option[];
    tooltip?: string | Element;
    onChangeCallback?: (value: any) => void;
    inputProps?: InputProps;
  };

type Option = { label: string; value: string | number };

export const SimpleSelect = <FormSchema,>({
  options = [],
  name,
  label,
  helper,
  placeholder = '-',
  variant,
  onChangeCallback,
  width = 'full',
  inputProps = {},
  ...rest
}: SimpleSelectProps<FormSchema>) => {
  const focusRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const { simpleOnBlur } = useSimpleFormContext();
  const { control, setValue } = useFormContext();
  const value = useWatch({ name, control });

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const onBlur = useCallback(
    (event: any) => {
      const option = options?.find((node) => node?.value === value);

      if (option?.value) {
        setValue(name, option?.value as any);
        onChangeCallback?.(option?.value);
      }

      setIsFocused(false);
      setFilteredOptions(options);

      simpleOnBlur?.(event);
    },
    [options, setValue, name, onChangeCallback, simpleOnBlur, value],
  );

  const onFocus = () => {
    setIsFocused(true);
  };

  useOutsideClick({
    ref: focusRef,
    handler: () => setIsFocused(false),
  });

  const onChange = (event: any) => {
    const searchTerm = event.target.value;
    setValue(name, searchTerm);
    const query = searchTerm.toLowerCase();

    if (!query) setFilteredOptions(options);

    const updatedOptions = options.filter(({ label }) => label?.toLowerCase().includes(query));

    setFilteredOptions(updatedOptions);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isFocused) return;

    const opt = filteredOptions?.[highlightedIndex];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (opt) {
          setValue(name, opt.value as any);
          onChangeCallback?.(opt.value);
          setIsFocused(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsFocused(false);
        break;
    }
  };

  const handleItemSelection = (nextValue: string | number) => () => {
    const option = options?.find((node) => node?.value === nextValue);
    setValue(name, option?.value as any);
    setIsFocused(false);
    onChangeCallback?.(option?.value);
  };

  const formattedValue = useMemo(() => {
    const option = options?.find((node) => node?.value === value);

    return option?.label ?? value ?? '';
  }, [options, value]);

  const renderListItem = ({ label, value }: Option, idx: number) => (
    <Box
      px={4}
      py={2}
      borderBottom="border.light"
      key={value}
      onMouseDown={handleItemSelection(value)}
      onMouseEnter={() => setHighlightedIndex(idx)}
      bg={idx === highlightedIndex ? 'bg.subtle' : undefined}
      cursor="pointer"
    >
      <Text fontSize="md">{label}</Text>
    </Box>
  );

  return (
    <>
      <Flex ref={focusRef} position="relative" w={width}>
        <SimpleField
          name={name}
          label={label}
          helper={helper}
          w="full"
          {...rest}
          variant={variant}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={onChange}
          onClick={onFocus}
        >
          <Stack direction="row" align="center" justify="space-between" borderRadius="md">
            <Input
              variant="unstyled"
              placeholder={placeholder}
              value={formattedValue}
              onChange={() => {}}
              autoComplete="off"
              fontSize="md"
              _placeholder={{ color: 'text.placeholder' }}
              onKeyDown={handleKeyDown}
              isRequired={rest?.isRequired}
              {...inputProps}
            />
            <Icon as={HiChevronDown} />
          </Stack>
        </SimpleField>
        <Show if={isFocused}>
          <Stack
            spacing={0}
            bg="bg.surface"
            maxH="md"
            overflowY="auto"
            position="absolute"
            zIndex={1}
            top={73}
            w="full"
            shadow="2xl"
            borderRadius="md"
          >
            {filteredOptions?.map(renderListItem)}
          </Stack>
        </Show>
      </Flex>
    </>
  );
};
